import React, { useState } from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useTabContext } from '../../contexts/TabContext';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Chrome-style tab counter button for mobile
 * Shows number of open tabs and opens tab switcher
 */
function MobileTabCounter() {
  const isMobile = useIsMobile();
  const { tabs, activeTabId, switchTab, closeTab } = useTabContext();
  const [showSwitcher, setShowSwitcher] = useState(false);

  if (!isMobile) {
    return null;
  }

  const tabCount = tabs?.length || 0;

  const handleTabClick = (tabId) => {
    switchTab(tabId);
    setShowSwitcher(false);
  };

  const handleCloseTab = (e, tabId) => {
    e.stopPropagation();
    closeTab(tabId);
  };

  return (
    <>
      {/* Tab counter button */}
      <button
        onClick={() => setShowSwitcher(!showSwitcher)}
        className="fixed bottom-20 right-4 z-40 w-12 h-12 rounded-lg border-2 border-gray-400 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg flex items-center justify-center text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        style={{
          marginBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        aria-label={`${tabCount} open tabs`}
      >
        {tabCount}
      </button>

      {/* Tab switcher overlay */}
      {showSwitcher && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={() => setShowSwitcher(false)}
          />

          {/* Tab list */}
          <div className="fixed inset-x-0 bottom-0 z-[70] bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl max-h-[70vh] overflow-y-auto"
            style={{
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
            }}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Open Tabs ({tabCount})
              </h2>
              <button
                onClick={() => setShowSwitcher(false)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close tab switcher"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Tab list */}
            <div className="p-4 space-y-2">
              {tabs.map((tab) => {
                const isActive = tab.id === activeTabId;
                return (
                  <div
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    className={`relative p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      isActive
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-medium truncate ${
                          isActive
                            ? 'text-blue-700 dark:text-blue-300'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {tab.title}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                          {tab.route}
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleCloseTab(e, tab.id)}
                        className="ml-2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        aria-label={`Close ${tab.title}`}
                      >
                        <CloseIcon className="text-gray-500 dark:text-gray-400" fontSize="small" />
                      </button>
                    </div>
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-500 rounded-r-full" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default MobileTabCounter;
